import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Tab } from "semantic-ui-react";
import { Button, Input } from "components/shared";

// Plain <p>/<b> here, not the shared <Text> component - Text's default
// color comes from theme.textDark (meant for the app's own dark
// background), but semantic-ui's <Modal> always renders on a fixed white
// background regardless of app theme, so in dark mode that color turned
// into light-gray-on-white, nearly unreadable (field report 2026-09-19).
// Matches the same plain-tag convention already used inside the other
// semantic-ui Modal in this app, components/shared/parametersTable's edit
// dialog - default text color there is dark, which is exactly what a
// permanently-white modal actually needs.
const InfoRow = ({ label, value }) => (
  <p style={{ margin: "0.4rem 0" }}>
    <b>{label}: </b>
    {value || "N/A"}
  </p>
);

const TurbineInfoModal = ({ open, onClose, windTurbine, errorText, onSendEmail, onSaveEmail, sending, savingEmail }) => {
  const { name, identifier, maxPower, type, email } = windTurbine || {};
  const [sent, setSent] = useState(false);
  const [emailInput, setEmailInput] = useState(email || "");

  // windTurbine.email only settles once the turbine has actually loaded -
  // sync the editable field whenever it changes (e.g. modal reopened for a
  // different turbine, or the value we just saved comes back from the API).
  useEffect(() => {
    setEmailInput(email || "");
  }, [email]);

  const handleSendEmail = async () => {
    setSent(false);
    const success = await onSendEmail();
    setSent(!!success);
  };

  const panes = [
    {
      menuItem: "Info",
      render: () => (
        <Tab.Pane>
          <InfoRow label="Serial number" value={identifier} />
          <InfoRow label="Model" value={type && type.name} />
          <InfoRow label="Schema" value={type && type.schemaId} />
          <InfoRow label="Putere nominală" value={maxPower ? `${maxPower} kW` : undefined} />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Email erori",
      render: () => (
        <Tab.Pane>
          <InfoRow label="Eroare curentă" value={errorText || "Fără eroare activă"} />
          <div style={{ padding: "0.4rem 0" }}>
            <b>Email notificare: </b>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem", alignItems: "center" }}>
              <Input
                type="email"
                placeholder="email@exemplu.com"
                value={emailInput}
                onChange={(e, data) => setEmailInput(data.value)}
                style={{ flex: 1 }}
              />
              <Button
                secondary
                disabled={savingEmail || emailInput === (email || "")}
                onClick={() => onSaveEmail(emailInput)}
              >
                {savingEmail ? "Se salvează..." : "Salvează"}
              </Button>
            </div>
          </div>
          {email && errorText && (
            <Button primary disabled={sending} onClick={handleSendEmail} style={{ marginTop: "0.75rem" }}>
              {sending ? "Se trimite..." : "Trimite eroarea pe email"}
            </Button>
          )}
          {email && !errorText && (
            <p style={{ color: "gray" }}>Nu există nicio eroare activă de trimis.</p>
          )}
          {sent && <span style={{ color: "green", marginLeft: "0.75rem" }}>Email trimis.</span>}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Modal size="small" open={open} onClose={onClose}>
      <Modal.Header>{name}</Modal.Header>
      <Modal.Content>
        <Tab panes={panes} />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} positive>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

TurbineInfoModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  windTurbine: PropTypes.shape({}),
  errorText: PropTypes.string,
  onSendEmail: PropTypes.func,
  onSaveEmail: PropTypes.func,
  sending: PropTypes.bool,
  savingEmail: PropTypes.bool,
};

TurbineInfoModal.defaultProps = {
  open: false,
  onClose: () => {},
  windTurbine: {},
  errorText: "",
  onSendEmail: () => Promise.resolve(false),
  onSaveEmail: () => {},
  sending: false,
  savingEmail: false,
};

export default TurbineInfoModal;
